import { app } from "@microsoft/teams-js";
import { StandardPlanLevels, TeamsSubscriptionPlans } from "@teams/types";
import {
  CouponDTO,
  NewStripeSubscriptionRequest,
  StripeBillingExternalShareUrlDTO,
  StripeBillingInfoDTO,
  StripeSubscriptionChangeDTO,
  UpgradeStripeSubscription,
} from "@teams/types/api";

import {
  FetchError,
  FetchResult,
  encodeApiUrl,
  fetchApiWithContext,
  fetchWithToken,
} from "../../_shared/services/ChannelApi";

export const paymentsApi = {
  validateCoupon: (
    tokenOrContext: string | app.Context,
    coupon: string
  ): Promise<FetchResult<CouponDTO>> => {
    const url = encodeApiUrl`/v1/billing/stripe/coupon/validate`;
    const requestData = {
      method: "POST",
      body: JSON.stringify({ coupon }),
    };

    if (typeof tokenOrContext === "string") {
      return fetchWithToken<CouponDTO | FetchError>(url, tokenOrContext, requestData);
    } else {
      return fetchApiWithContext<CouponDTO | FetchError>(tokenOrContext, url, requestData);
    }
  },

  createStripeSubscription: (
    tokenOrContext: string | app.Context,
    subscriptionDetails: NewStripeSubscriptionRequest
  ) => {
    const url = encodeApiUrl`/v1/billing/stripe/subscription`;
    const requestData = {
      method: "POST",
      body: JSON.stringify(subscriptionDetails),
    };

    if (typeof tokenOrContext === "string") {
      return fetchWithToken<{ id: string; trialEnd?: string; message?: string } | FetchError>(
        url,
        tokenOrContext,
        requestData
      );
    } else {
      return fetchApiWithContext<{ id: string; trialEnd?: string; message?: string } | FetchError>(
        tokenOrContext,
        url,
        requestData
      );
    }
  },

  updateStripePayment: (
    token: string,
    paymentId: string,
    subscriptionId: string
  ): Promise<FetchResult<Error>> => {
    return fetchWithToken(encodeApiUrl`/v1/billing/stripe/subscription/${subscriptionId}`, token, {
      method: "POST",
      body: JSON.stringify({
        paymentId,
      }),
    });
  },

  reactivateStripeSub: (
    context: app.Context,
    subscriptionId: string
  ): Promise<FetchResult<Error>> => {
    return fetchApiWithContext(
      context,
      encodeApiUrl`/v1/billing/stripe/subscription/${subscriptionId}`,
      {
        method: "POST",
        body: JSON.stringify({
          reactivate: true,
        }),
      }
    );
  },

  getStripeSubscriptionBillingInfo: (
    context: app.Context,
    subscriptionId: string
  ): Promise<FetchResult<Error> | StripeBillingInfoDTO> => {
    return fetchApiWithContext(
      context,
      encodeApiUrl`/v1/billing/stripe/subscription/${subscriptionId}`,
      {
        method: "GET",
      }
    );
  },

  upgradeSubscription: (
    context: app.Context,
    subscriptionId: string,
    body: UpgradeStripeSubscription
  ) => {
    return fetchApiWithContext(
      context,
      encodeApiUrl`/v1/billing/stripe/subscription/${subscriptionId}`,
      {
        method: "PUT",
        body: JSON.stringify(body),
      }
    );
  },

  getSubscriptionUpgradeEstimate: (
    context: app.Context,
    subscriptionId: string,
    args: UpgradeStripeSubscription
  ) => {
    const queryParams = new URLSearchParams();
    queryParams.set("licenses", args.licenses.toString());
    queryParams.set("frequency", args.frequency);
    queryParams.set("planId", args.planId);

    return fetchApiWithContext<StripeSubscriptionChangeDTO>(
      context,
      encodeApiUrl`/v1/billing/stripe/subscription/${subscriptionId}/estimate-change-cost?${queryParams.toString()}`,
      { queryParams }
    );
  },

  cancelSubscription: async (
    context: app.Context,
    subscriptionId: string,
    reason: string,
    feedback: string
  ): Promise<FetchResult<Error>> => {
    return fetchApiWithContext(
      context,
      encodeApiUrl`/v1/billing/stripe/subscription/${subscriptionId}/cancel`,
      { method: "POSt", body: JSON.stringify({ reason, feedback }) }
    );
  },

  checkUserSubscription: (
    token: string,
    ownerId: string,
    plan: TeamsSubscriptionPlans,
    search: string
  ): Promise<FetchResult<Error | { hasSubscription: boolean }>> => {
    return fetchWithToken(encodeApiUrl`/v1/billing/stripe/subscription/check`, token, {
      method: "POST",
      body: JSON.stringify({
        ownerId,
        plan,
        search,
      }),
    });
  },

  getSignedShareLink: async (
    token: string,
    ownerId: string,
    ownerName: string,
    ownerUpn: string,
    plan: string,
    planType: string,
    frequency: string,
    licenses: string
  ): Promise<FetchResult<StripeBillingExternalShareUrlDTO>> => {
    const result: FetchResult<StripeBillingExternalShareUrlDTO> = await fetchWithToken(
      encodeApiUrl`/v1/billing/stripe/subscription/generate-share-lnk`,
      token,
      {
        method: "POST",
        body: JSON.stringify({
          ownerId,
          ownerName,
          ownerUpn,
          plan,
          planType,
          frequency,
          licenses,
        }),
      }
    );
    return result;
  },

  getPricing: async (tokenOrContext: string | app.Context) => {
    const url = encodeApiUrl`/v1/billing/plans`;

    if (typeof tokenOrContext === "string") {
      return fetchWithToken<StandardPlanLevels[]>(url, tokenOrContext);
    } else {
      return fetchApiWithContext<StandardPlanLevels[]>(tokenOrContext, url);
    }
  },
};
